import { Divider, Stack } from '@mui/material';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ActionsMenu,
  Link,
  ListTotal,
  Table,
  TableToolbar,
  TableWrapper,
  Typography,
} from 'melp-design/components';
import { APP, ROUTES } from 'config';
import { useMe } from 'state/Administrators';
import {
  CompaniesFilter,
  FilterButton,
  Filters,
  OpenSearchFilter,
  SelectFilter,
} from 'components/filters';
import { useHistory } from 'react-router-dom';
import { useSearchParams } from 'utils/navigation';
import {
  COLOR_BY_STATUS,
  companiesFiltersSchema,
  companyStatuses,
  useCompanies,
} from 'store/companies';

export const CompaniesList: FC = () => {
  const { t } = useTranslation();
  const { canEditCompanies, hasEditPermission } = useMe();
  const history = useHistory();

  const { searchParams: filters, navigate } = useSearchParams(
    companiesFiltersSchema,
  );

  const { data: companies, isLoading } = useCompanies(filters);

  return (
    <TableWrapper>
      <TableToolbar>
        <Stack gap={1}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="flex-end"
            gap={1}
          >
            <ActionsMenu
              type="spread"
              items={[
                {
                  label: t('companies.new_company'),
                  disabled:
                    !canEditCompanies || !hasEditPermission(['companies']),
                  color: 'primary',
                  onClick: () => {
                    history.push(ROUTES.companies.create);
                  },
                },
              ]}
            />
          </Stack>

          <Divider />

          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            gap={2}
          >
            <ListTotal total={companies?.total} />

            <Filters
              value={filters}
              initialValues={companiesFiltersSchema.parse({})}
              onChange={(v) => navigate({ params: { ...v, page: 1 } })}
            >
              {({ value, initialValues, setFilter }) => (
                <>
                  <FilterButton
                    label={t('employees.company')}
                    value={value.companies}
                    initialValue={initialValues.companies}
                    onChange={(v) => setFilter('companies', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <CompaniesFilter
                        value={value}
                        onApplyFilter={applyFilter}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                      />
                    )}
                  </FilterButton>

                  <FilterButton
                    label={t('common.status')}
                    value={value.status}
                    initialValue={initialValues.status}
                    onChange={(v) => setFilter('status', v)}
                  >
                    {({ value, applyFilter, clearFilter, close }) => (
                      <SelectFilter
                        value={{ value }}
                        onApplyFilter={({ value }) => applyFilter(value)}
                        onClearFilter={clearFilter}
                        onCancel={close}
                        multiple
                        options={companyStatuses.map((status) => ({
                          label: t(`status.${status}`),
                          key: status,
                        }))}
                        searchable={false}
                      />
                    )}
                  </FilterButton>

                  <OpenSearchFilter
                    value={value.search}
                    onApplyFilter={(v) => setFilter('search', v)}
                    placeholder={t('common.search')}
                  />
                </>
              )}
            </Filters>
          </Stack>
        </Stack>
      </TableToolbar>

      <Table
        data={companies?.items ?? []}
        isLoading={isLoading}
        columns={[
          {
            id: 'name',
            header: t('companies.company_name'),
            cell: ({ row: { original: company } }) => {
              return (
                <Link
                  label={company.name}
                  to={ROUTES.companies.details.replace(':id', company.id)}
                />
              );
            },
            meta: { sort: true },
          },
          {
            id: 'employeeCount',
            header: t('employees.employee_count'),
            cell: ({ row: { original: company } }) => {
              return company.employeeCount;
            },
            meta: { sort: true, align: 'right' },
          },
          {
            id: 'departmentCount',
            header: t('companies.department_count'),
            cell: ({ row: { original: company } }) => {
              return company.departmentCount;
            },
            meta: { sort: true, align: 'right' },
          },
          {
            id: 'status',
            header: t('common.status'),
            cell: ({ row: { original: company } }) => {
              return (
                <Typography color={COLOR_BY_STATUS[company.status]}>
                  {t(`status.${company.status}`)}
                </Typography>
              );
            },
            meta: { sort: true },
          },
        ]}
        rows={{
          inactive: ({ original: company }) => company.status === 'inactive',
        }}
        sort={{
          value: filters.sort,
          onChange: (v) => navigate({ params: { sort: v } }),
        }}
        pagination={
          companies && {
            count: companies.total,
            page: companies.page,
            pageSize: companies.pageSize,
            pageSizeOptions: APP.pagination.defaults.pageSize,
            onChange: (page, pageSize) => {
              navigate({ params: { page, pageSize } });
            },
          }
        }
      />
    </TableWrapper>
  );
};
